import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle } from "lucide-react";
import { balanceAt, fmtMoney } from "@/lib/finance";
import { toast } from "sonner";

const CHANNELS = ["Wire", "Crypto", "Institutional Banking"];

export const TrancheModal = ({ open, onOpenChange, onCreate }) => {
  const todayDate = new Date().toISOString().slice(0, 10);
  const nowTime = new Date().toISOString().slice(11, 16);
  const [form, setForm] = useState({
    principal_amount: "", date: todayDate, time: nowTime,
    channel: "Wire", tx_reference: "", daily_rate: 5,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Gentle guardrail: a date far in the past compounds into a huge balance.
  const daysAgo = (() => {
    const t = new Date(`${form.date}T${form.time || "00:00"}:00Z`).getTime();
    if (Number.isNaN(t)) return 0;
    return (Date.now() - t) / 86400000;
  })();
  const showStaleWarning = daysAgo > 30;
  const amtNum = parseFloat(form.principal_amount);
  const rateNum = (parseFloat(form.daily_rate) || 5) / 100;
  const projectedNow = amtNum > 0 ? balanceAt(amtNum, rateNum, Math.max(daysAgo, 0)) : 0;

  const submit = async () => {
    const amt = parseFloat(form.principal_amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid principal amount"); return; }
    const iso = new Date(`${form.date}T${form.time}:00Z`).toISOString();
    try {
      await onCreate({
        principal_amount: amt,
        injection_datetime: iso,
        channel: form.channel,
        tx_reference: form.tx_reference || null,
        daily_rate: (parseFloat(form.daily_rate) || 5) / 100,
      });
      toast.success("Tranche logged — compounding started");
      onOpenChange(false);
      setForm((f) => ({ ...f, principal_amount: "", tx_reference: "" }));
    } catch (e) {
      toast.error("Failed to log tranche");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="tranche-injection-modal" className="bg-[#121722] border-[#232D40] text-slate-100 sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-amber-400">Capital Tranche Injection</DialogTitle>
          <DialogDescription className="text-sm text-slate-400">Log capital handed to Spencer Firm. Compounding begins from the exact timestamp.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <Label className="text-slate-300">Principal Amount ($)</Label>
            <Input
              data-testid="tranche-amount-input" type="number" placeholder="50000.00"
              value={form.principal_amount} onChange={(e) => set("principal_amount", e.target.value)}
              className="bg-[#0B0E17] border-[#232D40] font-mono-data text-lg h-11"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label className="text-slate-300">Date Injected</Label>
              <Input
                data-testid="tranche-date-input" type="date" value={form.date}
                onChange={(e) => set("date", e.target.value)}
                className="bg-[#0B0E17] border-[#232D40] font-mono-data"
              />
            </div>
            <div className="grid gap-2">
              <Label className="text-slate-300">Exact Time (UTC)</Label>
              <Input
                data-testid="tranche-time-input" type="time" value={form.time}
                onChange={(e) => set("time", e.target.value)}
                className="bg-[#0B0E17] border-[#232D40] font-mono-data"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label className="text-slate-300">Transfer Channel</Label>
              <Select value={form.channel} onValueChange={(v) => set("channel", v)}>
                <SelectTrigger data-testid="tranche-channel-select" className="bg-[#0B0E17] border-[#232D40]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121722] border-[#232D40] text-slate-100">
                  {CHANNELS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label className="text-slate-300">Daily Rate (%)</Label>
              <Input
                data-testid="tranche-rate-input" type="number" value={form.daily_rate}
                onChange={(e) => set("daily_rate", e.target.value)}
                className="bg-[#0B0E17] border-[#232D40] font-mono-data"
              />
            </div>
          </div>
          <div className="grid gap-2">
            <Label className="text-slate-300">Transaction Hash / Reference</Label>
            <Input
              data-testid="tranche-hash-input" placeholder="0x... / Wire Ref"
              value={form.tx_reference} onChange={(e) => set("tx_reference", e.target.value)}
              className="bg-[#0B0E17] border-[#232D40] font-mono-data"
            />
          </div>
          {showStaleWarning && (
            <div data-testid="tranche-stale-warning" className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 flex gap-3">
              <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
              <div className="text-xs text-amber-200/90 leading-relaxed">
                This date is <span className="font-semibold">{Math.floor(daysAgo)} days</span> in the past. At {(rateNum * 100).toFixed(1)}% daily compounding, {amtNum > 0 ? fmtMoney(amtNum, 0) : "the principal"} would already show as{" "}
                <span className="font-semibold font-mono-data">{fmtMoney(projectedNow, 0)}</span> today. Double-check the date (especially the year) so your balance isn't accidentally inflated.
              </div>
            </div>
          )}
          <Button
            data-testid="tranche-submit-btn" onClick={submit}
            className="bg-amber-500 hover:bg-amber-400 text-[#0B0E17] font-semibold h-11 mt-1"
          >
            Inject Capital & Start Compounding
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
